/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { numberToPercentageString } from 'src/core/utils/number';
import { resultCardStyles, resultCardTitleStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { SentenceAssessment, SentenceTarget } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';

export type AssessmentCardProps = { targets: SentenceTarget[]; assessments: SentenceAssessment[]; isTargetCard: boolean };

const messages = defineMessages({
    opinion: {
        id: 'languageStudio.assessmentCard.opinion',
        defaultMessage: 'Opinion'
    },
    target: {
        id: 'languageStudio.assessmentCard.target',
        defaultMessage: 'Target'
    },
    targets: {
        id: 'languageStudio.assessmentCard.targets',
        defaultMessage: 'Targets'
    },
    assessment: {
        id: 'languageStudio.assessmentCard.assessment',
        defaultMessage: 'Assessment'
    },
    assessments: {
        id: 'languageStudio.assessmentCard.assessments',
        defaultMessage: 'Assessments'
    }
});

const theme = getScopedTheme('SentimentDemoPage');

const itemsStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { display: 'block', paddingLeft: '20px' } });
const assessmentCardStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { width: '200px', minHeight: '150px' }
});

export const AssessmentCard = (props: AssessmentCardProps) => {
    const { targets, assessments, isTargetCard } = props;
    const { localize } = useLocalization();

    let target: SentenceTarget = null;
    let assessment: SentenceAssessment = null;
    let items: (SentenceTarget | SentenceAssessment)[] = [];

    if (isTargetCard) {
        [target] = targets;
        items = assessments;
    } else {
        [assessment] = assessments;
        items = targets;
    }

    return (
        <Stack
            tokens={{ childrenGap: 7 }}
            styles={mergeStyleSets(resultCardStyles, assessmentCardStyles)}
            key={`${target?.offset ?? assessment?.offset}${target?.length ?? assessment?.length}`}>
            <LuisText fontWeight={500} styles={resultCardTitleStyles(theme.labelColor)}>
                {localize(messages.opinion)}
            </LuisText>

            <LuisText>
                <LuisText variant="smallPlus" fontWeight={500}>
                    {localize(isTargetCard ? messages.target : messages.assessment)}:
                </LuisText>
                &nbsp;
                <LuisText variant="smallPlus">{isTargetCard ? target.text : assessment.text}</LuisText>
            </LuisText>

            <Stack>
                <LuisText variant="smallPlus" fontWeight={500}>
                    {localize(isTargetCard ? messages.assessments : messages.targets)}:
                </LuisText>

                <LuisText>
                    {items.map((item: SentenceAssessment | SentenceTarget) => (
                        <LuisText key={`${item.offset}${item.length}`} styles={itemsStyles} variant="smallPlus">
                            {item.text} ({item.sentiment}, {numberToPercentageString({ ratio: item.confidenceScores[item.sentiment] })})
                        </LuisText>
                    ))}
                </LuisText>
            </Stack>
        </Stack>
    );
};
